import React, { useState, useMemo } from "react";
import { Typography, Grid, Box, CircularProgress, Button } from "@mui/material";
import { NoSsr } from "@mui/base";
import BarComponent from "../components/BarComponent/BarComponent";

import { TransactionTable } from "../components/TransactionTable";
import DownloadPopup from "../components/DownloadPopup/DownloadPopup";
import { useTransactions } from "../hooks/useTransactions/useTransactions";
import { useAccelContext } from "../contexts/AccelContext/AccelContext";

import { SelectChangeEvent } from "@mui/material/Select";
import { Category } from "../types/Category";

export const TransactionsView = () => {
  const [toggle, setToggleType] = React.useState("transactions");
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [classifiedPopupOpen, setClassifiedPopupOpen] = useState(false);
  const { theme, authErrorState } = useAccelContext();

  if (authErrorState) {
    return null;
  }

  const {
    transactions,
    loadingStatus,
    mutate,
    classifiedTransactions,
    ctLoadingStatus,
    ctIsValidating,
    ctRefetch,
    ctError,
    ctMutate,
  } = useTransactions();

  const values = [{ value: "transactions", label: "Transactions" }];

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setSelectedRows(new Set());
  };

  const handleAccountChange = (event: SelectChangeEvent<string>) => {
    setSelectedAccount(event.target.value);
    setSelectedRows(new Set());
  };

  const filteredTransactions = useMemo(() => {
    return transactions
      ? transactions.filter(
          (transaction) =>
            selectedRows.has(transaction.id) ||
            searchTerm === "" ||
            transaction.description
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.amount.toString().includes(searchTerm) ||
            transaction.currency
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.bank_transaction_id
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.transaction_type
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.ledger_type
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.balance?.toString().includes(searchTerm) ||
            transaction.counterparty_name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.category?.name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            transaction.id.toString().includes(searchTerm)
        )
      : [];
  }, [transactions, selectedRows, searchTerm]);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Grid container spacing={2} sx={{ height: "100%", width: "100%" }}>
        <Grid
          item
          container
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
          }}
          width="100%"
          spacing={2}
        >
          <Grid item xs={12} sm="auto">
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: theme?.text?.hex,
                fontSize: { xs: 32, sm: 41 },
                fontFamily: "Helvetica Neue",
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              Transactions
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <BarComponent
            showButtons={true}
            showToggleGroup={true}
            setDownloadPopup={setDownloadPopup}
            showSearchBar={true}
            showDownloadButton={true}
            selectedAccount={selectedAccount}
            onAccountChange={handleAccountChange}
            values={values}
            toggleType={toggle}
            setToggleType={setToggleType}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            showDatePicker={false}
          />
        </Grid>
        <Grid item xs={12} sx={{ height: { xs: "auto", md: "auto" } }}>
          <NoSsr>
            {loadingStatus === "loading" ||
            !filteredTransactions ||
            filteredTransactions.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <CircularProgress sx={{ color: theme?.primary?.hex }} />
              </Box>
            ) : (
              <Box sx={{ width: "100%" }}>
                {toggle === "transactions" ? (
                  <TransactionTable
                    mutate={mutate}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    transactions={filteredTransactions}
                  />
                ) : (
                  <TransactionTable
                    mutate={mutate}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    transactions={filteredTransactions}
                  />
                )}
                <DownloadPopup
                  open={downloadPopup}
                  onClose={() => {
                    setDownloadPopup(false);
                  }}
                  transactions={
                    selectedRows.size > 0
                      ? filteredTransactions.filter((transaction) =>
                          selectedRows.has(transaction.id)
                        )
                      : filteredTransactions
                  }
                />
              </Box>
            )}
          </NoSsr>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransactionsView;
